import { useState } from 'react'
import { Plus, Edit, Trash2, Building, Power, PowerOff, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import clientesService from '../../services/clientesService'
import { formatCep, formatCpfCnpj, formatPhone } from '../../utils/masks'
import { fetchCEP, fetchCNPJ, isValidCNPJ } from '../../utils/validators'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import ConfirmDialog from '../ui/ConfirmDialog'
import CidadeAutocomplete from '../ui/CidadeAutocomplete'

export default function FazendasTab({ cliente, organizadorId, onUpdate }) {
  const { organizadorAtivo } = useOrganizador()
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo'
  const labelPlural = isFluxoCavalo ? 'Haras' : 'Fazendas'
  const labelPluralLower = isFluxoCavalo ? 'haras' : 'fazendas'
  const novoBtnLabel = isFluxoCavalo ? 'Novo Haras' : 'Nova Fazenda'
  const editarLabel = isFluxoCavalo ? 'Editar Haras' : 'Editar Fazenda'
  const excluirLabel = isFluxoCavalo ? 'Excluir Haras' : 'Excluir Fazenda'
  const nomeLabel = isFluxoCavalo ? 'Nome do Haras *' : 'Nome da Fazenda *'
  const nomePlaceholder = isFluxoCavalo ? 'Ex: Haras Boa Vista' : 'Ex: Fazenda São João'
  const nenhumLabel = isFluxoCavalo ? 'Nenhum haras cadastrado' : 'Nenhuma fazenda cadastrada'
  const adicioneLabel = isFluxoCavalo ? 'Adicione haras para este cliente' : 'Adicione fazendas para este cliente'
  const artigoItem = isFluxoCavalo ? 'o haras' : 'a fazenda'
  const [showModal, setShowModal] = useState(false)
  const [editingFazenda, setEditingFazenda] = useState(null)
  const [cnpjError, setCnpjError] = useState('')
  const [cnpjLoading, setCnpjLoading] = useState(false)
  const [cnpjStatus, setCnpjStatus] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    inscricao_estadual: '',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    telefone: '',
    senha_nota: '',
    observacoes: ''
  })

  // Cliente ainda não salvo
  if (!cliente) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg">
        <Building className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 text-sm">Salve o cliente primeiro para gerenciar {labelPluralLower}.</p>
      </div>
    )
  }

  const handleNew = () => {
    setEditingFazenda(null)
    setCnpjError('')
    setCnpjStatus('')
    setFormData({
      nome: '',
      cnpj: '',
      inscricao_estadual: '',
      endereco: '',
      numero: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: '',
      telefone: '',
      senha_nota: '',
      observacoes: ''
    })
    setShowModal(true)
  }

  const handleEdit = (fazenda) => {
    setEditingFazenda(fazenda)
    setFormData({
      nome: fazenda.nome || '',
      cnpj: fazenda.cnpj || '',
      inscricao_estadual: fazenda.inscricao_estadual || '',
      endereco: fazenda.endereco || '',
      numero: fazenda.numero || '',
      bairro: fazenda.bairro || '',
      cidade: fazenda.cidade || '',
      estado: fazenda.estado || '',
      cep: fazenda.cep || '',
      telefone: fazenda.telefone || '',
      senha_nota: fazenda.senha_nota || '',
      observacoes: fazenda.observacoes || ''
    })
    setShowModal(true)
  }

  const handleCnpjChange = async (value) => {
    const formatted = formatCpfCnpj(value)
    setFormData({ ...formData, cnpj: formatted })
    setCnpjError('')
    setCnpjStatus('')

    const digits = formatted.replace(/[^\d]/g, '')
    if (digits.length === 14) {
      setCnpjLoading(true)
      try {
        const data = await fetchCNPJ(formatted)
        if (data) {
          setCnpjStatus(data.situacao || '')
          setFormData(prev => ({
            ...prev,
            cnpj: formatted,
            nome: prev.nome || data.nome || '',
            telefone: prev.telefone || data.telefone || '',
            endereco: data.endereco || prev.endereco,
            numero: data.numero || prev.numero,
            bairro: data.bairro || prev.bairro,
            cidade: data.cidade || prev.cidade,
            estado: data.estado || prev.estado,
            cep: data.cep ? formatCep(data.cep) : prev.cep,
          }))
        }
      } finally {
        setCnpjLoading(false)
      }
    }
  }

  const handleCepChange = async (cep) => {
    const formattedCep = formatCep(cep)
    setFormData({...formData, cep: formattedCep})

    if (formattedCep.replace(/[^\d]/g, '').length === 8) {
      const cepData = await fetchCEP(formattedCep)
      if (cepData) {
        setFormData(prev => ({
          ...prev,
          endereco: cepData.endereco || prev.endereco,
          bairro: cepData.bairro || prev.bairro,
          cidade: cepData.cidade || prev.cidade,
          estado: cepData.estado || prev.estado
        }))
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validar CNPJ se preenchido
    if (formData.cnpj && !isValidCNPJ(formData.cnpj)) {
      setCnpjError('CNPJ inválido')
      return
    }
    setCnpjError('')
    
    try {
      if (editingFazenda) {
        await clientesService.updateFazenda(organizadorId, cliente.id, editingFazenda.id, formData)
      } else {
        await clientesService.createFazenda(organizadorId, cliente.id, formData)
      }
      setShowModal(false)
      onUpdate()
    } catch (error) {
      console.error('Erro ao salvar fazenda:', error)
      const errorMessage = error.response?.data?.detail || 'Erro ao salvar fazenda. Tente novamente.'
      toast.error(errorMessage)
    }
  }

  const handleDelete = (fazenda) => {
    setConfirmDelete(fazenda)
  }

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return
    
    try {
      await clientesService.deleteFazenda(organizadorId, cliente.id, confirmDelete.id)
      onUpdate()
    } catch (error) {
      console.error('Erro ao excluir fazenda:', error)
      const errorMessage = error.response?.data?.detail || 'Erro ao excluir fazenda. Verifique se não há vínculos.'
      toast.error(errorMessage)
    }
    setConfirmDelete(null)
  }

  const handleToggleStatus = async (fazenda) => {
    try {
      await clientesService.updateFazenda(organizadorId, cliente.id, fazenda.id, {
        ...fazenda,
        is_active: !fazenda.is_active
      })
      onUpdate()
    } catch (error) {
      console.error('Erro ao alterar status da fazenda:', error)
      const errorMessage = error.response?.data?.detail || 'Erro ao alterar status da fazenda.'
      toast.error(errorMessage)
    }
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">{labelPlural}</h3>
          <button
            onClick={handleNew}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            {novoBtnLabel}
          </button>
        </div>

        {!cliente?.fazendas || cliente.fazendas.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Building className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">{nenhumLabel}</h4>
            <p className="text-gray-500 mb-4">{adicioneLabel}</p>
            <button onClick={handleNew} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              {novoBtnLabel}
            </button>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CNPJ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cidade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Telefone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cliente?.fazendas?.map((fazenda) => (
                  <tr key={fazenda.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{fazenda.nome}</div>
                      {fazenda.endereco && (
                        <div className="text-sm text-gray-500">
                          {fazenda.endereco}, {fazenda.numero} - {fazenda.bairro}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {fazenda.cnpj || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {fazenda.cidade ? `${fazenda.cidade} - ${fazenda.estado}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {fazenda.telefone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        fazenda.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {fazenda.is_active ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(fazenda)}
                          className="text-blue-600 hover:text-blue-700 p-1 rounded"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(fazenda)}
                          className={`p-1 rounded ${
                            fazenda.is_active 
                              ? 'text-orange-600 hover:text-orange-700' 
                              : 'text-green-600 hover:text-green-700'
                          }`}
                          title={fazenda.is_active ? 'Inativar' : 'Ativar'}
                        >
                          {fazenda.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete(fazenda)}
                          className="text-red-600 hover:text-red-700 p-1 rounded"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-50" onClick={() => setShowModal(false)}></div>
            
            <div className="relative bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingFazenda ? editarLabel : novoBtnLabel}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Linha 1: Nome da Fazenda */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {nomeLabel}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className="input-rematei"
                      placeholder={nomePlaceholder}
                    />
                  </div>

                  {/* Linha 2: CNPJ e Inscrição Estadual */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CNPJ
                        {cnpjLoading && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-500 font-normal">
                            <Loader2 className="w-3 h-3 animate-spin" /> Buscando...
                          </span>
                        )}
                        {cnpjStatus && !cnpjLoading && (
                          <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            cnpjStatus === 'ATIVA'
                              ? 'bg-emerald-100 text-emerald-700'
                              : cnpjStatus === 'BAIXADA' || cnpjStatus === 'INAPTA'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            {cnpjStatus}
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={formData.cnpj}
                        onChange={(e) => handleCnpjChange(e.target.value)}
                        className={`input-rematei ${cnpjError ? 'border-red-500' : ''}`}
                        placeholder="00.000.000/0000-00"
                        maxLength="18"
                      />
                      {cnpjError && (
                        <p className="text-red-500 text-sm mt-1">{cnpjError}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Inscrição Estadual
                      </label>
                      <input
                        type="text"
                        value={formData.inscricao_estadual}
                        onChange={(e) => setFormData({...formData, inscricao_estadual: e.target.value})}
                        className="input-rematei"
                        placeholder="Inscrição Estadual"
                      />
                    </div>
                  </div>

                  {/* Linha 3: CEP, Telefone e Senha Nota */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CEP
                      </label>
                      <input
                        type="text"
                        value={formData.cep}
                        onChange={(e) => handleCepChange(e.target.value)}
                        className="input-rematei"
                        placeholder="00000-000"
                        maxLength="9"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone
                      </label>
                      <input
                        type="text"
                        value={formData.telefone}
                        onChange={(e) => {
                          const formatted = formatPhone(e.target.value)
                          setFormData({...formData, telefone: formatted})
                        }}
                        className="input-rematei"
                        placeholder="(00) 00000-0000"
                        maxLength="15"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Senha Nota
                      </label>
                      <input
                        type="text"
                        value={formData.senha_nota}
                        onChange={(e) => setFormData({...formData, senha_nota: e.target.value})}
                        className="input-rematei"
                        placeholder="Senha para emissão de nota"
                      />
                    </div>
                  </div>

                  {/* Linha 4: Endereço e Número */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Endereço
                      </label>
                      <input
                        type="text"
                        value={formData.endereco}
                        onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                        className="input-rematei"
                        placeholder="Rua, Avenida..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Número
                      </label>
                      <input
                        type="text"
                        value={formData.numero}
                        onChange={(e) => setFormData({...formData, numero: e.target.value})}
                        className="input-rematei"
                        placeholder="123"
                      />
                    </div>
                  </div>

                  {/* Linha 5: Bairro, Cidade e Estado */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bairro
                      </label>
                      <input
                        type="text"
                        value={formData.bairro}
                        onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                        className="input-rematei"
                        placeholder="Centro"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cidade
                      </label>
                      <CidadeAutocomplete
                        value={formData.cidade}
                        onChange={(cidade) => setFormData(prev => ({...prev, cidade}))}
                        onEstadoChange={(estado) => setFormData(prev => ({...prev, estado}))}
                        placeholder="Digite a cidade..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estado
                      </label>
                      <input
                        type="text"
                        maxLength="2"
                        value={formData.estado}
                        onChange={(e) => setFormData({...formData, estado: e.target.value.toUpperCase()})}
                        className="input-rematei"
                        placeholder="UF"
                        readOnly
                      />
                    </div>
                  </div>

                  {/* Linha 6: Observações */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observações
                    </label>
                    <textarea
                      value={formData.observacoes}
                      onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                      className="input-rematei"
                      rows="2"
                      placeholder="Observações sobre a fazenda..."
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary">
                      {editingFazenda ? 'Atualizar' : 'Criar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={confirmDeleteAction}
        title={excluirLabel}
        message={`Tem certeza que deseja excluir ${artigoItem} "${confirmDelete?.nome}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </>
  )
}